// Regla única de las líneas de venta de un evento de UN DÍA (U-10, lote 2 de
// la auditoría del flujo de un día, octubre de 2026). Réplica exacta, céntimo
// a céntimo, de `backend/lineas_venta.py`: de ella salen el total guardado,
// el PDF, la proforma, la factura y el cobro previo. La ficha la usa solo como
// vista previa mientras se edita; en cuanto llega la respuesta del servidor
// enseña sus importes (el navegador enseña, no decide).
//
// - Venta de una línea: precio_unitario (aunque sea 0) x personas > precio_ud
//   x uds > precio_por_persona x personas > precio_persona x personas;
//   personas por defecto, las del presupuesto. Sin ningún precio, el del menú
//   del catálogo (precio_persona > precio_por_persona > precio_venta; nunca el
//   coste).
// - IVA: tipo_iva > iva_percent > iva (texto "21" vale, el 0 se respeta, una
//   proporción 0,10 es el 10 %); sin IVA, 10 %. Suplementos: iva > tipo_iva >
//   iva_percent, 10 % por defecto.
// - Base y cuota redondeadas por línea a 2 decimales (mitad hacia arriba);
//   totales = suma de las líneas. Suplementos no cortesía, también negativos.
//
// Funciones puras, sin React: se prueban con node (scripts/test_lineas_venta.mjs).

export const IVA_POR_DEFECTO = 10;
export const CAMPOS_IVA_LINEA = ['tipo_iva', 'iva_percent', 'iva'];
export const CAMPOS_IVA_SUPLEMENTO = ['iva', 'tipo_iva', 'iva_percent'];
const IVA_EXTRAS = 21;

const vacio = (v) => v === null || v === undefined || v === '';

const num = (v, defecto = 0) => {
  if (vacio(v) || typeof v === 'boolean') return defecto;
  const n = Number(v);
  return Number.isFinite(n) ? n : defecto;
};

// Redondeo a céntimos, mitad hacia arriba (alejándose de cero), con el mismo
// margen y las mismas operaciones que `redondear2` del servidor
export const redondear2 = (valor) => {
  const v = num(valor);
  const signo = v < 0 ? -1 : 1;
  return (signo * Math.floor(Math.abs(v) * 100 + 0.5 + 1e-9)) / 100;
};

const enteroSiPuede = (n) => (Number.isInteger(n) ? n : n);

const valorTipoIva = (v) => {
  if (vacio(v) || typeof v === 'boolean') return null;
  let texto = v;
  if (typeof texto === 'string') {
    texto = texto.trim().replace('%', '').replace(',', '.');
    if (texto === '') return null;
  }
  let n = Number(texto);
  if (!Number.isFinite(n) || n < 0) return null;
  // Proporción guardada a la antigua (0,10 = 10 %)
  if (n > 0 && n < 1) n *= 100;
  return enteroSiPuede(Math.round(n * 1e4) / 1e4);
};

// IVA (%) explícito de una línea o suplemento; `defecto` si no tiene ninguno
export const tipoIvaDe = (d, campos = CAMPOS_IVA_LINEA, defecto = IVA_POR_DEFECTO) => {
  for (const campo of campos) {
    const n = valorTipoIva((d || {})[campo]);
    if (n !== null) return n;
  }
  return defecto;
};

// Precio de venta por persona de un menú del catálogo (nunca el coste)
export const precioMenuCatalogo = (menu) => {
  for (const campo of ['precio_persona', 'precio_por_persona', 'precio_venta']) {
    const v = num((menu || {})[campo]);
    if (v) return v;
  }
  return null;
};

// Venta sin IVA de una línea (réplica de multievento_utils.venta_de_linea)
export const ventaDeLinea = (linea, personasDefecto = 0) => {
  const l = linea || {};
  const personas = vacio(l.num_personas) ? num(personasDefecto) : num(l.num_personas);
  let precio;
  let unidades;
  if (!vacio(l.precio_unitario)) {
    precio = num(l.precio_unitario);
    unidades = personas;
  } else if (num(l.precio_ud) && num(l.uds)) {
    precio = num(l.precio_ud);
    unidades = num(l.uds);
  } else if (num(l.precio_por_persona)) {
    precio = num(l.precio_por_persona);
    unidades = personas;
  } else if (num(l.precio_persona)) {
    precio = num(l.precio_persona);
    unidades = personas;
  } else {
    return { base: 0, unidades: personas, precio: null };
  }
  return { base: precio * unidades, unidades, precio };
};

const esLineaAlquiler = (m) => !!(m && (m.es_alquiler || String(m.menu_id || '').startsWith('alquiler-')));
const esLineaBebida = (m) => !!(m && (m.es_bebida || String(m.menu_id || '').startsWith('bebida:')));

const linea = (concepto, cantidad, precio, base, tipo, origen, lineaId, indice) => {
  const cuota = redondear2((base * tipo) / 100);
  return {
    concepto,
    cantidad: vacio(cantidad) ? 1 : num(cantidad),
    precio_unitario: precio,
    base,
    tipo_iva: tipo,
    cuota,
    total: redondear2(base + cuota),
    origen,
    linea_id: lineaId ?? null,
    indice,
  };
};

// Líneas de venta y totales de un evento de un día (mismo resultado que
// `lineas_venta_un_dia` del servidor). Opciones: `catalogo` {menu_id: menú}
// para las líneas sin precio, `ivaCatalogo` {menu_id: IVA} para las líneas sin
// IVA (solo jornadas de un multievento) e `incluirExtras`.
export const lineasVentaUnDia = (presupuesto, { catalogo = null, ivaCatalogo = null, incluirExtras = false } = {}) => {
  const p = presupuesto || {};
  const personasEvento = p.num_personas;
  const lineas = [];
  (p.menus_personalizados || []).forEach((m, indice) => {
    if (!m || typeof m !== 'object') return;
    const venta = ventaDeLinea(m, personasEvento);
    let { precio } = venta;
    let bruto = venta.base;
    if (precio === null) {
      precio = precioMenuCatalogo((catalogo || {})[m.menu_id]) || 0;
      bruto = precio * venta.unidades;
    }
    let tipo = tipoIvaDe(m, CAMPOS_IVA_LINEA, null);
    if (tipo === null && ivaCatalogo && m.menu_id && m.menu_id in ivaCatalogo) tipo = valorTipoIva(ivaCatalogo[m.menu_id]);
    if (tipo === null) tipo = IVA_POR_DEFECTO;
    const origen = esLineaAlquiler(m) ? 'alquiler' : (esLineaBebida(m) ? 'bebida' : 'menu');
    lineas.push(linea(m.nombre_menu || m.nombre || 'Menú', venta.unidades, precio, redondear2(bruto), tipo, origen,
      m.linea_id, indice));
  });
  (p.suplementos || []).forEach((s, indice) => {
    if (!s || typeof s !== 'object' || s.es_cortesia) return;
    const base = redondear2(num(s.total));
    const tipo = tipoIvaDe(s, CAMPOS_IVA_SUPLEMENTO, IVA_POR_DEFECTO);
    const unidades = s.unidades || s.cantidad || 1;
    const precio = vacio(s.precio_unitario) ? base : num(s.precio_unitario);
    lineas.push(linea(s.nombre || s.concepto || 'Suplemento', unidades, precio, base, tipo, 'suplemento', s.id, indice));
  });
  if (incluirExtras && Array.isArray(p.extras)) {
    p.extras.forEach((e, indice) => {
      if (!e || typeof e !== 'object') return;
      const base = redondear2(num(e.total) || (num(e.precio_unitario) * num(e.cantidad, 1)));
      if (base <= 0) return;
      const tipo = tipoIvaDe(e, CAMPOS_IVA_LINEA, IVA_EXTRAS);
      const precio = vacio(e.precio_unitario) ? base : num(e.precio_unitario, base);
      lineas.push(linea(e.nombre || 'Suplemento', vacio(e.cantidad) ? 1 : e.cantidad, precio, base, tipo, 'extra', e.id, indice));
    });
  }
  let base = 0;
  let cuota = 0;
  let sup = 0;
  const porTipo = {};
  lineas.forEach((l) => {
    base += l.base;
    cuota += l.cuota;
    if (l.origen === 'suplemento') sup += l.base;
    const acc = porTipo[l.tipo_iva] || (porTipo[l.tipo_iva] = { base: 0, cuota: 0 });
    acc.base += l.base;
    acc.cuota += l.cuota;
  });
  base = redondear2(base);
  cuota = redondear2(cuota);
  const porTipoIva = {};
  Object.keys(porTipo).sort((a, b) => Number(a) - Number(b)).forEach((t) => {
    porTipoIva[t] = { base: redondear2(porTipo[t].base), cuota: redondear2(porTipo[t].cuota) };
  });
  return {
    lineas,
    base,
    cuota,
    total: redondear2(base + cuota),
    total_suplementos: redondear2(sup),
    por_tipo_iva: porTipoIva,
  };
};

// Vista previa de la ficha a partir de sus filas editables (`items`: precio_ud,
// uds, iva_percent) y los suplementos, con la misma regla de redondeo
export const totalesDesdeItems = (items, suplementos) => {
  let base = 0;
  let cuota = 0;
  (items || []).forEach((it) => {
    const b = redondear2(num(it?.precio_ud) * num(it?.uds));
    base += b;
    cuota += redondear2((b * num(it?.iva_percent)) / 100);
  });
  (suplementos || []).forEach((s) => {
    if (!s || typeof s !== 'object' || s.es_cortesia) return;
    const b = redondear2(num(s.total));
    base += b;
    cuota += redondear2((b * tipoIvaDe(s, CAMPOS_IVA_SUPLEMENTO, IVA_POR_DEFECTO)) / 100);
  });
  base = redondear2(base);
  cuota = redondear2(cuota);
  return { base, cuota, total: redondear2(base + cuota) };
};

// Línea nueva del presupuesto desde un menú del catálogo (U-11): la misma en
// el alta desde el listado y en "Añadir menú" de la ficha (los dos formatos).
// Precio: precio_persona > precio_por_persona > precio_venta > 0 (nunca el
// coste); IVA del catálogo o 10; platos y unidades por plato del catálogo.
// `platoActivo(id)` (opcional) filtra los platos dados de baja.
export const lineaDesdeMenu = (menu, personas, { platoActivo = null } = {}) => {
  const m = menu || {};
  const platosCatalogo = (m.platos_ids && m.platos_ids.length > 0)
    ? m.platos_ids
    : (m.platos || []).map(p => p && (p.plato_id || p.id)).filter(Boolean);
  const platos = typeof platoActivo === 'function' ? platosCatalogo.filter(platoActivo) : [...platosCatalogo];
  const linea = {
    menu_id: m.id,
    nombre_menu: m.nombre,
    platos_ids: platos,
    unidades_por_plato: { ...(m.unidades_por_plato || {}) },
    num_personas: personas,
    adaptaciones: [],
    es_personalizado: false,
    precio_unitario: precioMenuCatalogo(m) || 0,
    tipo_iva: tipoIvaDe(m, CAMPOS_IVA_LINEA, IVA_POR_DEFECTO),
    bebida_incluida: !!m.bebida_incluida,
    bebidas_por_persona: m.bebida_incluida ? (m.bebidas_por_persona || 1) : 0,
  };
  if (m.tipo_menu) linea.tipo_menu = m.tipo_menu;
  return linea;
};

// U-33: al cambiar las personas de la cabecera, qué líneas pasarían al número
// nuevo (las que tenían las anteriores o ninguna) y cuáles se dejan (otro
// número). Alquiler y bebidas (unidades propias) no cuentan. Mismo criterio
// que el servidor (`aplicar_personas_a_lineas`).
export const lineasAfectadasPorPersonas = (menus, anterior, nuevo) => {
  const aplicables = [];
  const distintas = [];
  const ant = Math.round(num(anterior));
  const nue = Math.round(num(nuevo));
  (menus || []).forEach((m, indice) => {
    if (!m || typeof m !== 'object' || esLineaAlquiler(m) || esLineaBebida(m)) return;
    const nombre = m.nombre_menu || m.nombre || 'Línea';
    if (vacio(m.num_personas) || Math.round(num(m.num_personas)) === ant) {
      aplicables.push({ indice, nombre_menu: nombre });
    } else if (Math.round(num(m.num_personas)) !== nue) {
      distintas.push({ indice, nombre_menu: nombre, num_personas: Math.round(num(m.num_personas)) });
    }
  });
  return { aplicables, distintas };
};

const fechaIso = (v) => {
  if (vacio(v)) return null;
  const t = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : null;
};

const hoyIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Líneas automáticas congeladas (E-10, U-12): mismo criterio que el servidor
// (`motivo_lineas_automaticas_congeladas`). Devuelve el texto del aviso o null.
export const textoLineasAutomaticasCongeladas = (p, hoy = null) => {
  const d = p || {};
  const estado = String(d.estado || '').toLowerCase();
  let motivo = null;
  if (estado === 'cancelado') motivo = 'el presupuesto está cancelado';
  else if (estado === 'rechazado') motivo = 'el presupuesto está rechazado';
  else if (estado === 'facturado' || d.tiene_factura || d.factura_id || d.numero_factura) motivo = 'el evento está facturado';
  else if (d.numero_proforma) motivo = 'el evento tiene una proforma emitida';
  else {
    const fechas = [fechaIso(d.fecha_evento)];
    if (d.es_multi_evento && Array.isArray(d.jornadas)) d.jornadas.forEach(j => fechas.push(fechaIso(j && j.fecha)));
    const validas = fechas.filter(Boolean).sort();
    if (validas.length > 0 && validas[validas.length - 1] < (hoy || hoyIso())) motivo = 'el evento ya se ha celebrado';
  }
  return motivo ? `Líneas automáticas congeladas: ${motivo}` : null;
};

// ¿Tiene el presupuesto alguna línea automática (mínimo 40, menú adaptado,
// Madrid Central, hora extra del lunch o barra libre del lunch vinculada)?
export const tieneLineasAutomaticas = (p) => {
  const d = p || {};
  const sups = (d.suplementos || []).some(s => s && (s.es_suplemento_minimo || s.es_suplemento_menu_adaptado
    || s.es_suplemento_madrid_central || s.es_suplemento_hora_extra_lunch));
  const barra = (d.menus_personalizados || []).some(m => m && m.es_barra_libre_lunch === true && m.barra_libre_de_linea_id);
  return sups || barra;
};
